import PlanogramDialog from '@/components/associate/PlanogramDialog';
import ScannerInputField, { ScannerInputFieldRef } from '@/mobile/components/ScannerInputField';
import ImageDialog from '@/components/associate/ImageDialog';
import DefaultLayoutDialog from '@/components/associate/DefaultLayoutDialog';
import BaseFontIcon from '@/components/ui/BaseFontIcon';
import BaseButton from '@/components/ui/BaseButton';
import { useTranslation } from 'react-i18next';
import { useMobileState } from '@/mobile/provider/context';
import {
    forwardRef,
    Ref,
    useCallback,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';
import { useAppContext } from '@/provider/contexts';
import { MULTI_LAYOUT_SLUG } from '@/types/pages/layout';
import { MobileFeatureHandle, MobileFeatureViewProps } from '@/mobile/types';
import { formatBarcode } from '@/utils/inputUtils';
import { isArray, isEmpty } from 'lodash';
import { fetchTagLayoutDetailsAPI, pushDevicesToProductsAPI } from '@/api/storeManagement/device';
import { fetchProductInfoAPI } from '@/api/storeManagement/shelfPlanogram';
import { ERROR_CODE_MAP, MULTI_LAYOUT_IMG, SETTING_FLAG } from '@/mobile/utils/mobileUtils';
import {
    deriveIds,
    deriveModeConfig,
    filterCategoryByCapacity,
    parseLpIdentifierInfo,
    splitLayoutsByCampaign,
} from '@/utils/associateUtils';
import { createOnScanHandler } from '@/mobile/utils/createOnScanHandler';
import { createEslInputHandler } from '@/mobile/utils/createEslInputHandler';
import { LayoutDetails, PushDeviceRequest } from '@/types/pages/storeManagement/device';
import { ASSOCIATE_PAIRING_MODE, ImageInfo, ShelfInfo } from '@/types/pages/storeManagement/device/associate';
import {
    Typography,
    Tooltip,
    SelectChangeEvent,
    Select,
    RadioGroup,
    Radio,
    MenuItem,
    FormControlLabel,
    DialogTitle,
    DialogContent,
    DialogActions,
    Dialog,
    Checkbox,
    CardMedia,
    Box,
} from '@mui/material';
import {
    reducer,
    defaultState,
    multiAssociationInitialImageInfo,
    multiAssociationInitialShelfInfo,
    MultiAssociationFlowContext,
} from './multiAssociationView.state';
import {
    createEmptyProductValues,
    getInitialPairingMode,
    isProductInputEnabledForMode,
} from './multiAssociationView.utils';
import { AssociationError } from '../association/associationView.state';

const MultiAssociationView = forwardRef(({ dependency }: MobileFeatureViewProps, ref: Ref<MobileFeatureHandle>) => {
    const { t } = useTranslation('common');
    const { manageStoreId, setNotification } = useAppContext();
    const { state } = useMobileState();
    const [viewState, dispatchView] = useReducer(reducer, defaultState);

    const {
        settings,
        refs: { isSendingRef, eslMacInputRef },
        method: { focusCommonEslInput, setESL, setButtonInfo, addHistoryItem, playScan },
    } = dependency;

    const {
        styleList,
        selectedStyle,
        capacity,
        productValues,
        multiLayoutInfo,
        multiDefaultCheck,
        defaultLayoutId,
        selectedPairingMode,
        planogramConfig,
        campaignEnable,
        layoutDialogOpen,
        planogramDialogOpen,
        imageDialogOpen,
        shelfInfo,
        imageInfo,
        previewOpen,
        isProductInputEnabled,
        identifierInfo,
        pendingAssociationContext,
    } = viewState;

    const subLayoutList = useMemo(() => {
        if (!selectedStyle || !styleList.length) return [];
        const style = styleList.find((item) => item.id === selectedStyle);
        if (!style) return [];
        return (multiLayoutInfo[style.slug] || []) as LayoutDetails[];
    }, [multiLayoutInfo, selectedStyle, styleList]);

    const {
        modeSubLayoutList,
        hasNormalModeSublayout,
        hasImageModeSublayout,
        hasAnyModeSublayout,
        hasDisabledPairingOption,
    } = useMemo(() => {
        const { campaign, nonCampaign } = splitLayoutsByCampaign(subLayoutList);

        return {
            modeSubLayoutList: selectedPairingMode === ASSOCIATE_PAIRING_MODE.NORMAL ? nonCampaign : campaign,
            hasNormalModeSublayout: nonCampaign.length > 0,
            hasImageModeSublayout: campaignEnable && campaign.length > 0,
            hasAnyModeSublayout: nonCampaign.length > 0 || (campaignEnable && campaign.length > 0),
            hasDisabledPairingOption: nonCampaign.length === 0 || (campaignEnable && campaign.length === 0),
        };
    }, [selectedPairingMode, campaignEnable, subLayoutList]);

    const [pairingHintOpen, setPairingHintOpen] = useState(false);
    const productInputRefs = useRef<Array<ScannerInputFieldRef | null>>([]);
    const [focusProductTick, setFocusProductTick] = useState(0);
    const focusTargetIndexRef = useRef(0);

    useLayoutEffect(() => {
        if (!isProductInputEnabled) return;

        const targetIndex = focusTargetIndexRef.current;
        requestAnimationFrame(() => {
            productInputRefs.current[targetIndex]?.focus();
        });
    }, [focusProductTick, isProductInputEnabled]);

    const focusProductInput = useCallback((index?: number) => {
        if (index !== undefined && index >= 0) {
            focusTargetIndexRef.current = index;
        }

        setFocusProductTick((prev) => prev + 1);
    }, []);

    const resetMultiAssociation = useCallback(() => {
        setESL('');
        dispatchView({ type: 'RESET_ALL' });

        setTimeout(() => {
            focusCommonEslInput();
        }, 150);
    }, [focusCommonEslInput, setESL]);

    const fetchTagInfo = useCallback(
        async (finalMac: string) => {
            if (!manageStoreId) return;

            isSendingRef.current = true;
            setButtonInfo({ type: 'loading' });

            try {
                const {
                    data: { data, isSuccess, status, message },
                } = await fetchTagLayoutDetailsAPI(manageStoreId, [finalMac]);

                if (isSuccess) {
                    const { category, device } = data || {};
                    const {
                        multi_layouts,
                        is_campaign_enabled,
                        is_planogram_enabled,
                        planogram_autoselectsingleopt,
                        planogram_show_nooption,
                        lp_identifier,
                    } = device?.[0] || {};

                    const styles = filterCategoryByCapacity(category);
                    if (!styles.length) {
                        setButtonInfo({ type: 'error', msg: 'Multi-product not supported. Use another ESL.' });
                        return;
                    }

                    const multiLayouts = multi_layouts || {};
                    if (isEmpty(multiLayouts)) {
                        setButtonInfo({ type: 'error', msg: 'No layout for ESL. Contact admin.' });
                        return;
                    }

                    const firstStyle = styles[0];
                    const firstStyleLayouts = (multiLayouts[firstStyle.slug] || []) as LayoutDetails[];
                    const nextCapacity = firstStyle.capacity;

                    const { campaign, nonCampaign } = splitLayoutsByCampaign(firstStyleLayouts);
                    const initialMode = getInitialPairingMode(nonCampaign, campaign);
                    const hasInitialModeSublayout = isProductInputEnabledForMode(initialMode, nonCampaign, campaign);
                    const nextIdentifierInfo = parseLpIdentifierInfo(lp_identifier);

                    setButtonInfo({ type: 'idle' });
                    dispatchView({
                        type: 'PATCH',
                        payload: {
                            styleList: styles,
                            selectedStyle: firstStyle.id,
                            capacity: nextCapacity,
                            productValues: createEmptyProductValues(nextCapacity),
                            multiLayoutInfo: multiLayouts,
                            selectedPairingMode: initialMode,
                            multiDefaultCheck: true,
                            defaultLayoutId: null,
                            campaignEnable: is_campaign_enabled,
                            isProductInputEnabled: hasInitialModeSublayout,
                            shelfInfo: multiAssociationInitialShelfInfo,
                            imageInfo: multiAssociationInitialImageInfo,
                            planogramConfig: {
                                enable: is_planogram_enabled,
                                noOption: SETTING_FLAG[planogram_show_nooption],
                                autoSelect: SETTING_FLAG[planogram_autoselectsingleopt],
                            },
                            identifierInfo: nextIdentifierInfo,
                            pendingAssociationContext: null,
                        },
                    });

                    focusProductInput(0);
                    return;
                }

                if (status === 400 || status === 401) {
                    const msg = ERROR_CODE_MAP[data.error_code];
                    throw new Error(msg);
                }

                throw new Error(message);
            } catch (error) {
                const errorReason = (error as Error).message;
                setButtonInfo({ type: 'error', msg: errorReason });
                dispatchView({
                    type: 'PATCH',
                    payload: {
                        isProductInputEnabled: false,
                        pendingAssociationContext: null,
                    },
                });
            } finally {
                isSendingRef.current = false;
            }
        },
        [focusProductInput, isSendingRef, manageStoreId, setButtonInfo],
    );

    const handleMultiAssociation = useCallback(
        async (context: MultiAssociationFlowContext) => {
            const { mac, productBarcodes, relations } = context;

            if (state.isSubmitting || !manageStoreId || !mac) {
                return;
            }

            if (productBarcodes.length !== capacity) {
                setButtonInfo({ type: 'error', msg: 'Please complete all product barcode inputs' });
                return;
            }

            const payload: PushDeviceRequest = {
                store_id: manageStoreId,
                macs: [mac],
                products: productBarcodes,
                multi_layout_skeleton_id: selectedStyle,
                overwrite_layout: defaultLayoutId ?? 'default',
            };

            if (selectedPairingMode === ASSOCIATE_PAIRING_MODE.NORMAL) {
                if (!isEmpty(relations) && planogramConfig.enable) {
                    payload.overwrite_shelf_codes = deriveIds(productBarcodes, relations);
                }
            }

            if (selectedPairingMode === ASSOCIATE_PAIRING_MODE.IMAGE) {
                if (!isEmpty(relations) && campaignEnable) {
                    payload.overwrite_campaign_id = deriveIds(productBarcodes, relations);
                }
            }

            isSendingRef.current = true;
            setButtonInfo({ type: 'sending' });

            try {
                const {
                    data: { data, isSuccess, message, status },
                } = await pushDevicesToProductsAPI(payload);

                const { product_information, allSuccess, pushed_barcodes, error_code, blocked } = data || {};
                const pushedBarcodes = pushed_barcodes || productBarcodes;
                const productInfoMap = !isArray(product_information) ? product_information : {};

                const productNames = pushedBarcodes.map((barcode: string) => {
                    return productInfoMap[barcode]?.name || 'Empty Product';
                });

                if (isSuccess && allSuccess) {
                    setButtonInfo({ type: 'success' });
                    addHistoryItem({
                        eslMac: mac,
                        productBarcode: pushedBarcodes.join(', '),
                        status: 'success',
                        timestamp: new Date().toLocaleString(),
                        productName: productNames.join(', '),
                        allSuccess: true,
                        containsEmptyProduct: productNames.some((name: string) => name === 'Empty Product'),
                    });
                    resetMultiAssociation();
                    return;
                }

                const failureDetails =
                    message?.trim() ||
                    blocked?.map((item: { reason: string }) => item.reason).join('; ') ||
                    'Unknown error occurred';

                if (status === 400 || status === 401) {
                    throw new AssociationError(failureDetails, {
                        code: error_code,
                        status,
                        historyData: {
                            mac,
                            productBarcode: pushedBarcodes.join(', '),
                            productName: productNames.join(', '),
                        },
                    });
                }

                throw new AssociationError(failureDetails, {
                    code: error_code,
                    status,
                });
            } catch (error) {
                if (error instanceof AssociationError) {
                    const { message, code, historyData } = error;

                    setNotification({
                        message,
                        severity: 'error',
                    });

                    setButtonInfo({ type: 'error', msg: 'failed' });

                    addHistoryItem({
                        eslMac: historyData?.mac ?? mac,
                        productBarcode: historyData?.productBarcode ?? productBarcodes.join(', '),
                        status: 'failed',
                        timestamp: new Date().toLocaleString(),
                        productName: historyData?.productName ?? 'Empty Product',
                        errorReason: code,
                        allSuccess: false,
                    });

                    resetMultiAssociation();
                    return;
                }

                const fallbackMessage = error instanceof Error ? error.message : 'Unknown error occurred';

                setNotification({
                    message: fallbackMessage,
                    severity: 'error',
                });
                setButtonInfo({ type: 'error', msg: 'failed' });
                resetMultiAssociation();
            } finally {
                isSendingRef.current = false;
            }
        },
        [
            manageStoreId,
            campaignEnable,
            capacity,
            defaultLayoutId,
            planogramConfig.enable,
            selectedPairingMode,
            selectedStyle,
            state.isSubmitting,
        ],
    );

    const handleShelfFlow = useCallback(
        async (context: MultiAssociationFlowContext, nextShelfInfo: Partial<ShelfInfo>) => {
            if (nextShelfInfo.shouldOpenDialog) {
                setButtonInfo({ type: 'idle' });
                dispatchView({
                    type: 'PATCH',
                    payload: {
                        planogramDialogOpen: true,
                        shelfInfo: { ...shelfInfo, ...nextShelfInfo },
                        pendingAssociationContext: context,
                    },
                });
                return;
            }

            await handleMultiAssociation({
                ...context,
                relations: nextShelfInfo.relations || {},
            });
        },
        [handleMultiAssociation, setButtonInfo, shelfInfo],
    );

    const handleNext = useCallback(
        async (
            context: MultiAssociationFlowContext,
            nextShelfInfo: Partial<ShelfInfo>,
            nextImageInfo: Partial<ImageInfo>,
        ) => {
            if (selectedPairingMode === ASSOCIATE_PAIRING_MODE.NORMAL) {
                if (nextShelfInfo.openFeature) {
                    await handleShelfFlow(context, nextShelfInfo);
                    return;
                }

                await handleMultiAssociation(context);
                return;
            }

            if (nextImageInfo.openFeature) {
                dispatchView({
                    type: 'PATCH',
                    payload: {
                        imageDialogOpen: true,
                        imageInfo: { ...imageInfo, ...nextImageInfo },
                        pendingAssociationContext: context,
                    },
                });
                return;
            }

            await handleMultiAssociation(context);
        },
        [handleMultiAssociation, handleShelfFlow, imageInfo, selectedPairingMode],
    );

    const fetchMultiProductInfo = useCallback(
        async (context: MultiAssociationFlowContext) => {
            if (!manageStoreId) return;

            if (context.productBarcodes.length !== capacity) {
                setButtonInfo({ type: 'error', msg: 'Please complete all product barcode inputs' });
                resetMultiAssociation();
                return;
            }

            isSendingRef.current = true;
            setButtonInfo({ type: 'loading' });

            try {
                const {
                    data: { data, isSuccess, message },
                } = await fetchProductInfoAPI(manageStoreId, context.productBarcodes);

                if (!isSuccess) {
                    throw new Error(message || 'Product fetch failed. Re-Scan.');
                }

                const { nextShelfInfo, nextImageInfo } = deriveModeConfig(
                    data.results,
                    planogramConfig,
                    campaignEnable,
                    identifierInfo,
                );

                if (selectedPairingMode === ASSOCIATE_PAIRING_MODE.IMAGE && !nextImageInfo.openFeature) {
                    setButtonInfo({ type: 'error', msg: 'No campaign image. Contact admin.' });
                    resetMultiAssociation();
                    return;
                }

                await handleNext(context, nextShelfInfo, nextImageInfo);
            } catch (error) {
                const errorReason = error instanceof Error ? error.message : 'Product fetch failed. Re-Scan.';
                setNotification({ message: errorReason, severity: 'error' });
                setButtonInfo({ type: 'error', msg: 'Failed' });
                resetMultiAssociation();
            } finally {
                isSendingRef.current = false;
            }
        },
        [
            campaignEnable,
            capacity,
            handleNext,
            identifierInfo,
            isSendingRef,
            manageStoreId,
            planogramConfig,
            resetMultiAssociation,
            selectedPairingMode,
            setButtonInfo,
            setNotification,
        ],
    );

    const handleProductEnter = useCallback(
        async (index: number, inputValue: string) => {
            const product = inputValue.trim();
            if (!product || !isProductInputEnabled || state.isSubmitting || !state.esl || !selectedStyle) {
                return;
            }

            const formatted = formatBarcode(
                settings.auto_convert_upce_to_upca === '1',
                settings.addLeadingZero === '1',
                product,
            );

            const next = [...productValues];
            next[index] = formatted;

            dispatchView({ type: 'PATCH', payload: { productValues: next } });

            if (index + 1 < capacity) {
                playScan();
                focusProductInput(index + 1);
                return;
            }

            const context: MultiAssociationFlowContext = {
                mac: state.esl,
                productBarcodes: next.map((value) => value.trim()).filter(Boolean),
            };

            await fetchMultiProductInfo(context);
        },
        [
            capacity,
            fetchMultiProductInfo,
            focusProductInput,
            isProductInputEnabled,
            playScan,
            productValues,
            settings.addLeadingZero,
            settings.auto_convert_upce_to_upca,
            state.esl,
            state.isSubmitting,
        ],
    );

    const handleStyleChange = (event: SelectChangeEvent<number>) => {
        const styleId = Number(event.target.value);
        const style = styleList.find((item) => item.id === styleId);
        if (!style) return;

        const nextCapacity = style.capacity;
        const nextProductValues = createEmptyProductValues(nextCapacity);

        const styleLayouts = (multiLayoutInfo[style.slug] || []) as LayoutDetails[];
        const { campaign, nonCampaign } = splitLayoutsByCampaign(styleLayouts);

        const nextMode = getInitialPairingMode(nonCampaign, campaign);
        const nextIsProductInputEnabled = isProductInputEnabledForMode(nextMode, nonCampaign, campaign);

        dispatchView({
            type: 'PATCH',
            payload: {
                selectedStyle: styleId,
                capacity: nextCapacity,
                productValues: nextProductValues,
                selectedPairingMode: nextMode,
                multiDefaultCheck: true,
                defaultLayoutId: null,
                isProductInputEnabled: nextIsProductInputEnabled,
                pendingAssociationContext: null,
            },
        });

        requestAnimationFrame(() => {
            focusProductInput(0);
        });
    };

    const onEslEnter = useMemo(
        () =>
            createEslInputHandler({
                isSubmitting: state.isSubmitting,
                setESL,
                setButtonInfo,
                eslMacInputRef,
                onInvalid: resetMultiAssociation,
                onValid: fetchTagInfo,
            }),
        [eslMacInputRef, fetchTagInfo, resetMultiAssociation, setButtonInfo, setESL, state.isSubmitting],
    );

    const onScan = useMemo(
        () =>
            createOnScanHandler({
                shouldProcess: () => !state.isSubmitting,
                onNormalizedScan: async (normalized) => {
                    if (!state.esl) {
                        await onEslEnter(normalized);
                        return;
                    }

                    if (!isProductInputEnabled) {
                        return;
                    }

                    const nextIndex = productValues.findIndex((value) => value.trim() === '');
                    if (nextIndex === -1) return;

                    await handleProductEnter(nextIndex, normalized);
                },
            }),
        [handleProductEnter, isProductInputEnabled, onEslEnter, productValues, state.esl, state.isSubmitting],
    );

    const onModeChange = useCallback(() => {
        if (state.esl && isProductInputEnabled) {
            focusProductInput(0);
            return;
        }

        focusCommonEslInput();
    }, [focusCommonEslInput, focusProductInput, isProductInputEnabled, state.esl]);

    const onClear = useCallback(() => {
        resetMultiAssociation();
    }, [resetMultiAssociation]);

    useImperativeHandle(
        ref,
        () => ({
            onEslEnter,
            onScan,
            onModeChange,
            onClear,
        }),
        [onClear, onEslEnter, onModeChange, onScan],
    );

    return (
        <>
            {styleList.length > 0 && (
                <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {t('Style Selection')}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Select
                            data-testid="multi-style-select"
                            size="small"
                            value={selectedStyle ?? styleList[0]?.id ?? ''}
                            onChange={(event) => handleStyleChange(event as SelectChangeEvent<number>)}
                            sx={{ flex: 1, minHeight: 36, backgroundColor: 'white' }}
                        >
                            {styleList.map((style) => (
                                <MenuItem key={style.id} value={style.id}>
                                    {style.name}
                                </MenuItem>
                            ))}
                        </Select>

                        <BaseButton
                            variant="outlined"
                            color="info"
                            onClick={() => dispatchView({ type: 'PATCH', payload: { previewOpen: true } })}
                        >
                            {t('Preview')}
                        </BaseButton>
                    </Box>
                </Box>
            )}

            {hasAnyModeSublayout && (
                <Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {t('Pairing Mode')}
                        </Typography>

                        {hasDisabledPairingOption && (
                            <Tooltip
                                title={t('Disabled: no layout')}
                                placement="top"
                                open={pairingHintOpen}
                                onClose={() => setPairingHintOpen(false)}
                                slotProps={{
                                    tooltip: {
                                        sx: {
                                            fontSize: '0.72rem',
                                        },
                                    },
                                }}
                                arrow
                            >
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        pl: 0.5,
                                    }}
                                    onClick={() => setPairingHintOpen((prev) => !prev)}
                                >
                                    <BaseFontIcon color="info" className="fa-regular fa-circle-info" size={16} />
                                </Box>
                            </Tooltip>
                        )}
                    </Box>

                    <RadioGroup
                        row
                        value={selectedPairingMode}
                        onChange={(event) => {
                            const mode = event.target.value as ASSOCIATE_PAIRING_MODE;

                            dispatchView({
                                type: 'PATCH',
                                payload: {
                                    selectedPairingMode: mode,
                                    isProductInputEnabled:
                                        mode === ASSOCIATE_PAIRING_MODE.NORMAL
                                            ? hasNormalModeSublayout
                                            : hasImageModeSublayout,
                                    pendingAssociationContext: null,
                                },
                            });

                            if (mode === ASSOCIATE_PAIRING_MODE.IMAGE) {
                                dispatchView({
                                    type: 'PATCH',
                                    payload: {
                                        multiDefaultCheck: true,
                                        defaultLayoutId: null,
                                    },
                                });
                            }
                        }}
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem', fontWeight: 700 } }}
                    >
                        <FormControlLabel
                            value={ASSOCIATE_PAIRING_MODE.NORMAL}
                            control={<Radio />}
                            label={t('Normal')}
                            disabled={!hasNormalModeSublayout}
                        />
                        {campaignEnable && (
                            <FormControlLabel
                                value={ASSOCIATE_PAIRING_MODE.IMAGE}
                                control={<Radio />}
                                label={t('Image')}
                                disabled={!hasImageModeSublayout}
                            />
                        )}
                    </RadioGroup>

                    {selectedPairingMode === ASSOCIATE_PAIRING_MODE.NORMAL && (
                        <FormControlLabel
                            sx={{ '& .MuiFormControlLabel-label': { m: 0, fontSize: '0.8rem', fontWeight: 700 } }}
                            control={
                                <Checkbox
                                    disabled={isSendingRef.current}
                                    checked={multiDefaultCheck}
                                    onChange={(event) => {
                                        const checked = event.target.checked;

                                        if (!checked) {
                                            dispatchView({
                                                type: 'PATCH',
                                                payload: {
                                                    multiDefaultCheck: false,
                                                    layoutDialogOpen: true,
                                                    pendingAssociationContext: null,
                                                },
                                            });
                                            return;
                                        }

                                        dispatchView({
                                            type: 'PATCH',
                                            payload: {
                                                multiDefaultCheck: true,
                                                defaultLayoutId: null,
                                                pendingAssociationContext: null,
                                            },
                                        });
                                    }}
                                />
                            }
                            label={t('Use Default Layout')}
                        />
                    )}
                </Box>
            )}

            {styleList.length > 0 && !hasAnyModeSublayout && (
                <Box
                    sx={{
                        mb: 1,
                        borderRadius: 1,
                        px: 1,
                        py: 0.75,
                        backgroundColor: '#fff4ce',
                        border: '1px solid #ffd699',
                    }}
                >
                    <Typography variant="body2" sx={{ color: '#6b4f00', fontWeight: 700 }}>
                        {t('No layout for ESL. Contact admin.')}
                    </Typography>
                </Box>
            )}

            <Box data-testid="multi-association-view-root">
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {t('Product Barcodes')}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {Array.from({ length: capacity }).map((_, index) => (
                        <Box
                            key={`multi-product-${index}`}
                            sx={{ display: 'flex', alignItems: 'stretch', gap: '0.5rem' }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 60,
                                    borderRadius: 1,
                                    backgroundColor: '#a3c585',
                                    color: 'white',
                                    fontWeight: 700,
                                }}
                            >
                                {index + 1}
                            </Box>

                            <ScannerInputField
                                ref={(instance) => {
                                    productInputRefs.current[index] = instance;
                                }}
                                inputTestId={
                                    index === 0
                                        ? 'multi-product-input-0'
                                        : index === 1
                                          ? 'multi-product-input-1'
                                          : `multi-product-input-${index}`
                                }
                                value={productValues[index] || ''}
                                setValue={(value) => {
                                    const next = [...productValues];
                                    next[index] = value;
                                    dispatchView({ type: 'PATCH', payload: { productValues: next } });
                                }}
                                disabled={!isProductInputEnabled}
                                onKeyDown={(event) => {
                                    if (!isProductInputEnabled) return;
                                    if (state.isSubmitting) return;
                                    if (event.key !== 'Enter') return;

                                    event.preventDefault();
                                    void handleProductEnter(index, (event.currentTarget as HTMLInputElement).value);
                                }}
                                onScannerScan={(value) => void handleProductEnter(index, value)}
                            />
                        </Box>
                    ))}
                </Box>
            </Box>

            <Dialog
                open={previewOpen}
                maxWidth="sm"
                fullWidth
                TransitionProps={{
                    onExited: () => focusProductInput(),
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    {styleList.find((item) => item.id === selectedStyle)?.name}
                </DialogTitle>

                <DialogContent>
                    <CardMedia
                        component="img"
                        image={
                            MULTI_LAYOUT_IMG[
                                styleList.find((item) => item.id === selectedStyle)?.slug as MULTI_LAYOUT_SLUG
                            ]
                        }
                        alt="Multi Layout Preview"
                        sx={{ height: 120, width: '100%', objectFit: 'contain' }}
                    />
                </DialogContent>

                <DialogActions sx={{ justifyContent: 'center' }}>
                    <BaseButton
                        variant="contained"
                        color="info"
                        onClick={() => dispatchView({ type: 'PATCH', payload: { previewOpen: false } })}
                    >
                        {t('OK')}
                    </BaseButton>
                </DialogActions>
            </Dialog>

            <DefaultLayoutDialog
                eslMac={state.esl}
                open={layoutDialogOpen}
                options={modeSubLayoutList.map((item) => ({
                    id: item.sub_layout_id,
                    name: item.sub_layout_name,
                    previewUrl: item.preview,
                    purpose: item.sub_layout_purpose,
                }))}
                onConfirm={(layoutId) =>
                    dispatchView({
                        type: 'PATCH',
                        payload: {
                            defaultLayoutId: layoutId,
                            multiDefaultCheck: false,
                            isProductInputEnabled: true,
                            layoutDialogOpen: false,
                        },
                    })
                }
                onClose={() => {
                    dispatchView({
                        type: 'PATCH',
                        payload: {
                            layoutDialogOpen: false,
                            multiDefaultCheck: defaultLayoutId == null ? true : multiDefaultCheck,
                        },
                    });
                }}
                onExited={focusProductInput}
            />

            <PlanogramDialog
                open={planogramDialogOpen}
                onClose={() => {
                    dispatchView({
                        type: 'PATCH',
                        payload: {
                            planogramDialogOpen: false,
                            pendingAssociationContext: null,
                        },
                    });
                    focusProductInput();
                    setButtonInfo({ type: 'idle' });
                }}
                planogramInfo={shelfInfo.options}
                relations={shelfInfo.relations}
                handleOnClick={async (relations) => {
                    const context = pendingAssociationContext;

                    dispatchView({
                        type: 'PATCH',
                        payload: {
                            planogramDialogOpen: false,
                            pendingAssociationContext: null,
                        },
                    });

                    if (!context) {
                        return;
                    }

                    await handleMultiAssociation({
                        ...context,
                        relations,
                    });
                }}
            />

            <ImageDialog
                open={imageDialogOpen}
                onClose={() => {
                    dispatchView({
                        type: 'PATCH',
                        payload: {
                            imageDialogOpen: false,
                            pendingAssociationContext: null,
                        },
                    });
                    focusProductInput();
                    setButtonInfo({ type: 'idle' });
                }}
                options={imageInfo.options}
                relations={imageInfo.relations}
                handleOnClick={async (relations) => {
                    const context = pendingAssociationContext;

                    dispatchView({
                        type: 'PATCH',
                        payload: {
                            imageDialogOpen: false,
                            pendingAssociationContext: null,
                        },
                    });

                    if (!context) {
                        return;
                    }

                    await handleMultiAssociation({
                        ...context,
                        relations,
                    });
                }}
            />
        </>
    );
});

export default MultiAssociationView;
